import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { MessageSquare, X, Send, Bot, Loader2, Maximize2, Minimize2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GoogleGenAI } from '@google/genai';
import { storeCurrentNavigationOrigin } from '../utils/navigationMemory';
import { executeRecaptchaV3 } from '../utils/recaptchaV3';

const geminiApiKey = import.meta.env.VITE_GEMINI_API_KEY ?? process.env.GEMINI_API_KEY ?? '';
const ai = geminiApiKey ? new GoogleGenAI({ apiKey: geminiApiKey }) : null;
const mainMenuOption = "Back to main menu";
const privacyPolicyOption = "Privacy Policy";
const termsConditionsOption = "Terms and Conditions";

const shortReplyRules = `
  Response style:
  - Be friendly, clear, and helpful.
  - Keep every answer under 70 words.
  - Use simple language. Avoid heavy jargon unless the user asks for technical detail.
  - Prefer 2-3 short bullets for explanations.
  - Answer the user's question first, then offer one useful next step.
  - Do not write long paragraphs, sales pitches, or repeated marketing claims.
`;

const buildFriendlySystemInstruction = (context: string) => `
  You are GallopBot, a friendly website assistant for AppGallop.
  AppGallop helps telecom, SaaS, cloud distribution, and managed service businesses automate commerce, billing, provisioning, renewals, and revenue operations.

  Product knowledge:
  - Cloud Marketplace: catalogs, partner selling, provisioning, subscriptions, and marketplace operations.
  - CPQ and renewals: quoting, pricing control, contract changes, and renewal workflows.
  - Unified Billing: usage, recurring charges, CDR mediation, invoices, tax, and ledger handoff.
  - Services: FinOps reconciliation and revenue assurance, BizOps process governance, managed support desk, consulting, and custom development for cloud/digital service operators.

  Current page context: ${context}

  Pricing rule:
  - If asked about pricing, say pricing depends on scale, integrations, transaction volume, and region. Suggest contacting the team.

  ${shortReplyRules}
`;

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

const personalEmailDomains = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'yahoo.co.in',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'zoho.com',
  'mail.com',
  'gmx.com',
  'gmx.net',
  'fastmail.com',
  'hey.com',
  'tutanota.com',
  'yandex.com',
  'rediffmail.com',
  'tempmail.com',
  '10minutemail.com',
  'guerrillamail.com',
  'mailinator.com'
]);

const isBusinessEmail = (email: string) => {
  const domain = email.trim().toLowerCase().split('@')[1];
  return Boolean(domain && !personalEmailDomains.has(domain));
};

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  options?: string[];
}

type FlowStep = 'IDLE' | 'DEMO_SERVICE' | 'DEMO_SIZE' | 'DEMO_EMAIL';
type LocalResponse = { content: string; options?: string[] };

export const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [flowStep, setFlowStep] = useState<FlowStep>('IDLE');
  const [leadData, setLeadData] = useState<{
    service?: string;
    size?: string;
    email?: string;
    location?: any;
  }>({});
  const scrollRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const initialOptions = [
    "Request for a Demo",
    "Know our Platform",
    "Explore Our Solutions",
    "About AppGallop"
  ];

  const demoFocusOptions = [
    "UCaaS",
    "CCaaS",
    "CPaaS",
    "Cloud Marketplace",
    "Managed Services"
  ];

  const demoScaleOptions = [
    "Under 10k users",
    "10k-100k users",
    "1M+ users"
  ];

  const platformNavOptions = [
    "Cloud Commerce Platform",
    "CPQ",
    "RMS"
  ];

  const platformRoutesByOption: Record<string, string> = {
    "Cloud Commerce Platform": "/products/marketplace",
    "CPQ": "/products/revsimply",
    "RMS": "/products/rms"
  };

  const solutionNavOptions = [
    "Telco to Techco",
    "Cloud Distribution",
    "SaaS ISV",
    "UCaaS/CCaaS Billing & BSS"
  ];

  const solutionRoutesByOption: Record<string, string> = {
    "Telco to Techco": "/industries/telco-to-techco",
    "Cloud Distribution": "/industries/cloud-distribution",
    "SaaS ISV": "/industries/saas-isv",
    "UCaaS/CCaaS Billing & BSS": "/products/uc-billing"
  };

  const legalRoutesByOption: Record<string, string> = {
    [privacyPolicyOption]: "/privacy-policy",
    [termsConditionsOption]: "/terms-and-conditions"
  };

  const withMainMenuOption = (options: string[] = []) =>
    options.includes(mainMenuOption) ? options : [...options, mainMenuOption];

  const getLocalGeneralResponse = (text: string): LocalResponse | null => {
    const query = text.trim().toLowerCase();

    if (!query) return null;

    if (/(privacy|policy|data protection|personal data)/.test(query)) {
      return {
        content: "You can review AppGallop's privacy information on the Privacy Policy page.",
        options: [privacyPolicyOption, termsConditionsOption]
      };
    }

    if (/(terms|condition|service agreement|legal)/.test(query)) {
      return {
        content: "You can review AppGallop's terms on the Terms and Conditions page.",
        options: [termsConditionsOption, privacyPolicyOption]
      };
    }

    if (/\b(hi|hello|hey|good morning|good evening|good afternoon)\b/.test(query)) {
      return {
        content: "Hi. I can help with AppGallop products, solutions, pricing basics, demos, contact details, and website pages.",
        options: initialOptions
      };
    }

    if (/(demo|contact|talk to|sales|call|meeting|book)/.test(query)) {
      return {
        content: "I can help you request a demo. Choose a focus area and I will collect the details for our team.",
        options: ["Request for a Demo"]
      };
    }

    if (/(price|pricing|cost|quote|subscription|license)/.test(query)) {
      return {
        content: "Pricing depends on scale, integrations, transaction volume, and region. The quickest route is to request a demo so the team can scope it properly.",
        options: ["Request for a Demo"]
      };
    }

    if (/(product|platform|marketplace|cpq|rms|renewal|commerce|billing)/.test(query)) {
      return {
        content: "AppGallop supports cloud commerce, CPQ, renewals, billing, provisioning, and revenue operations from one operating layer.",
        options: platformNavOptions
      };
    }

    if (/(solution|industry|telco|techco|cloud distribution|saas|isv|ucaas|ccaas)/.test(query)) {
      return {
        content: "AppGallop is built for telecom, SaaS ISV, cloud distribution, and UCaaS/CCaaS billing operations.",
        options: solutionNavOptions
      };
    }

    if (/(service|finops|bizops|support|consulting|development)/.test(query)) {
      return {
        content: "AppGallop services cover FinOps reconciliation, BizOps process governance, managed support, consulting, and custom development.",
        options: ["Explore Our Solutions", "Request for a Demo"]
      };
    }

    if (/(about|company|appgallop|who are you|what do you do)/.test(query)) {
      return {
        content: "AppGallop helps enterprise revenue teams automate digital commerce, billing, provisioning, renewals, support workflows, and lifecycle operations.",
        options: ["About AppGallop", "Know our Platform"]
      };
    }

    return null;
  };

  const getFallbackGeneralResponse = (): LocalResponse => ({
    content: "I can help with AppGallop products, solutions, demos, pricing basics, contact requests, privacy, and terms. Choose an option or ask a more specific question.",
    options: initialOptions
  });

  // Gather IP and Geo-location
  const fetchGeoData = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      return data;
    } catch (error) {
      console.warn("Could not fetch geo-data:", error);
      return { ip: 'Unknown', city: 'Unknown', country_name: 'Unknown' };
    }
  };

  const triggerLeadEmail = async (data: typeof leadData) => {
    try {
      const recaptchaAction = 'lead_chatbot';
      const recaptchaToken = await executeRecaptchaV3(recaptchaAction);
      const response = await fetch('/api/lead', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...data,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
          recaptchaToken,
          recaptchaAction,
        }),
      });
      
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        const result = await response.json();
        console.log("Backend response:", result);
      } else {
        const text = await response.text();
        console.warn("Server returned non-JSON response:", text);
      }
    } catch (error) {
      console.error("Failed to trigger lead email:", error);
    }
  };

  // Contextual information based on current route
  const getContext = () => {
    const path = location.pathname;
    if (path === '/') return 'Home Page - Providing high-level overview of AppGallop.';
    if (path.includes('/platform')) return 'Platform Page - Detailing core infrastructure like CRM, CPQ, and Billing.';
    if (path.includes('/services') || path.includes('/solutions')) return 'Services Page - Explaining FinOps reconciliation, BizOps, managed support desk, consulting, and custom development services.';
    if (path.includes('/products/')) return `Product Detail Page: ${path.split('/').pop()} - Deep dive into a specific AG product.`;
    if (path.includes('/industries/')) return `Industry Detail Page: ${path.split('/').pop()} - Specialized focus on a sector.`;
    return 'General AppGallop platform information.';
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Initial greeting
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: "Hi, I'm GallopBot. I can help you explore AppGallop, understand products, or request a demo. What would you like to do?",
          timestamp: new Date(),
          options: initialOptions
        }
      ]);
    }
  }, []);

  const showMainMenu = () => {
    setFlowStep('IDLE');
    setLeadData({});
    setInput('');
    setMessages(prev => [
      ...prev,
      { role: 'user', content: mainMenuOption, timestamp: new Date() },
      {
        role: 'assistant',
        content: "Sure. What would you like to do next?",
        timestamp: new Date(),
        options: initialOptions
      }
    ]);
  };

  const handleOptionClick = (option: string) => {
    if (option === mainMenuOption) {
      showMainMenu();
      return;
    }

    const directRoute = platformRoutesByOption[option] ?? solutionRoutesByOption[option] ?? legalRoutesByOption[option] ?? (option === "About AppGallop" ? "/platform/about" : undefined);
    if (directRoute) {
      setMessages(prev => [
        ...prev,
        { role: 'user', content: option, timestamp: new Date() },
        {
          role: 'assistant',
          content: `Redirecting to ${option}.`,
          timestamp: new Date(),
          options: [mainMenuOption]
        }
      ]);
      storeCurrentNavigationOrigin();
      navigate(directRoute);
      return;
    }

    if (flowStep !== 'IDLE') {
      setMessages(prev => [...prev, { role: 'user', content: option, timestamp: new Date() }]);
      handleDemoFlow(option);
      return;
    }

    if (option === "Request for a Demo") {
      setMessages(prev => [
        ...prev,
        { role: 'user', content: option, timestamp: new Date() },
        { 
          role: 'assistant', 
          content: "Choose a demo focus:",
          timestamp: new Date(),
          options: [...demoFocusOptions, mainMenuOption]
        }
      ]);
      setFlowStep('DEMO_SERVICE');
    } else if (option === "Know our Platform") {
      setMessages(prev => [
        ...prev,
        { role: 'user', content: option, timestamp: new Date() },
        {
          role: 'assistant',
          content: "Choose a platform:",
          timestamp: new Date(),
          options: [...platformNavOptions, mainMenuOption]
        }
      ]);
    } else if (option === "Explore Our Solutions") {
      setMessages(prev => [
        ...prev,
        { role: 'user', content: option, timestamp: new Date() },
        {
          role: 'assistant',
          content: "Choose a solution:",
          timestamp: new Date(),
          options: [...solutionNavOptions, mainMenuOption]
        }
      ]);
    } else {
      setInput(option);
      // Trigger handleSend immediately if possible or just populate input
      // Better to just send it
      setMessages(prev => [...prev, { role: 'user', content: option, timestamp: new Date() }]);
      processAIResponse(option);
    }
  };

  const processAIResponse = async (userInput: string) => {
    const localResponse = getLocalGeneralResponse(userInput);
    if (localResponse) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: localResponse.content,
        timestamp: new Date(),
        options: withMainMenuOption(localResponse.options)
      }]);
      return;
    }

    setIsLoading(true);
    if (!ai) {
      const fallbackResponse = getFallbackGeneralResponse();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: fallbackResponse.content,
        timestamp: new Date(),
        options: withMainMenuOption(fallbackResponse.options)
      }]);
      setIsLoading(false);
      return;
    }
    try {
      const context = getContext();
      const response = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: [
          ...messages.map(m => ({
            role: m.role === 'user' ? 'user' : 'model',
            parts: [{ text: m.content }]
          })),
          { role: 'user', parts: [{ text: userInput }] }
        ],
        config: {
          systemInstruction: buildFriendlySystemInstruction(context),
          temperature: 0.2,
          maxOutputTokens: 140,
        }
      });

      const text = response.text;
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: text || "Transmission error. Re-link required.",
        timestamp: new Date(),
        options: [mainMenuOption]
      }]);
    } catch (error) {
      console.error("AI Error:", error);
      const fallbackResponse = getFallbackGeneralResponse();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: fallbackResponse.content,
        timestamp: new Date(),
        options: withMainMenuOption(fallbackResponse.options)
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = input;
    setInput('');

    if (flowStep !== 'IDLE') {
      handleDemoFlow(currentInput);
      return;
    }

    const localResponse = getLocalGeneralResponse(currentInput);
    if (localResponse) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: localResponse.content,
        timestamp: new Date(),
        options: withMainMenuOption(localResponse.options)
      }]);
      return;
    }

    if (!ai) {
      const fallbackResponse = getFallbackGeneralResponse();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: fallbackResponse.content,
        timestamp: new Date(),
        options: withMainMenuOption(fallbackResponse.options)
      }]);
      return;
    }

    setIsLoading(true);

    try {
      const context = getContext();
      // Use the modern SDK pattern
      const response = await ai.models.generateContent({
        model: "gemini-3-flash-preview",
        contents: [
          ...messages.map(m => ({
            role: m.role === 'user' ? 'user' : 'model',
            parts: [{ text: m.content }]
          })),
          { role: 'user', parts: [{ text: currentInput }] }
        ],
        config: {
          systemInstruction: buildFriendlySystemInstruction(context),
          temperature: 0.25,
          maxOutputTokens: 140,
        }
      });

      const text = response.text;

      const assistantMessage: Message = {
        role: 'assistant',
        content: text || "I'm sorry, I encountered an issue processing that.",
        timestamp: new Date(),
        options: [mainMenuOption]
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Chatbot Error:", error);
      const fallbackResponse = getFallbackGeneralResponse();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: fallbackResponse.content,
        timestamp: new Date(),
        options: withMainMenuOption(fallbackResponse.options)
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoFlow = async (text: string) => {
    if (flowStep === 'DEMO_SERVICE') {
      setLeadData(prev => ({ ...prev, service: text }));
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Thanks. Select your approximate scale:",
        timestamp: new Date(),
        options: [...demoScaleOptions, mainMenuOption]
      }]);
      setFlowStep('DEMO_SIZE');
    } else if (flowStep === 'DEMO_SIZE') {
      setLeadData(prev => ({ ...prev, size: text }));
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Got it. Please share your company email, and our team will follow up with demo details.",
        timestamp: new Date(),
        options: [mainMenuOption]
      }]);
      setFlowStep('DEMO_EMAIL');
    } else if (flowStep === 'DEMO_EMAIL') {
      const email = text.trim();
      if (!isValidEmail(email)) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "Please enter a valid email address, like name@company.com.",
          timestamp: new Date(),
          options: [mainMenuOption]
        }]);
        return;
      }

      if (!isBusinessEmail(email)) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: "Please use your company email address so our team can verify the demo request.",
          timestamp: new Date(),
          options: [mainMenuOption]
        }]);
        return;
      }

      setIsLoading(true);
      const geo = await fetchGeoData();
      const finalLeadData = { ...leadData, email, location: geo };
      setLeadData(finalLeadData);
      
      await triggerLeadEmail(finalLeadData);
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Thanks. Your demo request has been sent to our team. Someone will contact you at ${email} shortly.`,
        timestamp: new Date(),
        options: [mainMenuOption]
      }]);
      setFlowStep('IDLE');
      setLeadData({});
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 left-6 sm:left-auto z-[1000] font-sans pointer-events-none">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className={`bg-white rounded-2xl shadow-2xl border border-black/10 overflow-hidden flex flex-col transition-all duration-300 pointer-events-auto ${
                isMinimized ? 'h-11 w-[calc(100%-48px)] sm:w-60' : 'h-[60vh] sm:h-[430px] w-[calc(100%-48px)] sm:w-72'
              }`}
          >
            {/* Header */}
            <div className="h-11 shrink-0 bg-slate-950 text-white flex items-center justify-between px-3 border-b border-white/5">
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-lg bg-[#ff5d22] flex items-center justify-center">
                  <Bot size={12} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-black uppercase tracking-widest leading-none">GallopBot</span>
                  <div className="flex items-center gap-1 mt-0.5">
                    <div className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-[8px] text-white/40 font-bold uppercase">Online</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button 
                  onClick={() => setIsMinimized(!isMinimized)}
                  className="p-1.5 hover:bg-white/10 rounded-md transition-colors text-white/60"
                  title={isMinimized ? "Expand" : "Minimize"}
                >
                  {isMinimized ? <Maximize2 size={12} /> : <Minimize2 size={12} />}
                </button>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="p-1.5 hover:bg-white/10 rounded-md transition-colors text-white/60"
                  title="Close Chat"
                >
                  <X size={12} />
                </button>
              </div>
            </div>

            {/* Chat Body */}
            {!isMinimized && (
              <>
                <div 
                  ref={scrollRef}
                  className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-[#f8fbff]"
                >
                  {messages.map((msg, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: msg.role === 'user' ? 8 : -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[90%] p-2.5 rounded-xl text-[11px] font-semibold leading-relaxed ${
                        msg.role === 'user' 
                          ? 'bg-[#ff5d22] text-white rounded-tr-none' 
                          : 'bg-white border border-black/5 text-slate-800 rounded-tl-none shadow-sm'
                      }`}>
                        {msg.content}
                        
                        {msg.options && (
                          <div className="mt-3 flex flex-col gap-2">
                            {msg.options.map((option, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleOptionClick(option)}
                                className="w-full text-left p-2 rounded-lg bg-slate-50 border border-black/5 hover:border-[#ff5d22]/30 hover:bg-blue-50 transition-all text-slate-600 font-bold text-[10px]"
                              >
                                {option}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                  {isLoading && (
                    <div className="flex justify-start">
                      <div className="bg-white border border-black/5 p-2 rounded-xl rounded-tl-none shadow-sm">
                        <Loader2 className="animate-spin text-[#ff5d22]" size={14} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Input Area */}
                <div className="p-2.5 border-t border-black/5 bg-white shrink-0">
                  <div className="relative flex items-center">
                    <input 
                      type={flowStep === 'DEMO_EMAIL' ? 'email' : 'text'} 
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                      placeholder={flowStep === 'DEMO_EMAIL' ? 'Enter company email...' : 'Type your question...'}
                      className="w-full bg-slate-50 border-none rounded-lg py-2 pl-3 pr-10 text-[11px] font-bold focus:ring-1 focus:ring-[#ff5d22]/20 transition-all placeholder:text-slate-400"
                    />
                    <button 
                      onClick={handleSend}
                      disabled={isLoading || !input.trim()}
                      className="absolute right-1 p-1.5 bg-slate-950 text-white rounded-md hover:bg-[#ff5d22] disabled:bg-slate-100 disabled:text-slate-300 transition-all"
                    >
                      <Send size={12} />
                    </button>
                  </div>
                  <div className="flex items-center justify-center gap-1.5 mt-2 opacity-30">
                    <div className="w-1 h-1 bg-black rounded-full" />
                    <p className="text-[7px] text-center text-black font-black uppercase tracking-[0.2em]">
                      AppGallop AI Assistant
                    </p>
                    <div className="w-1 h-1 bg-black rounded-full" />
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {!isOpen && (
        <motion.button
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setIsOpen(true)}
          className="w-12 h-12 rounded-full flex items-center justify-center shadow-2xl transition-all duration-300 bg-[#ff5d22] hover:bg-slate-900 text-white pointer-events-auto"
        >
          <MessageSquare size={20} />
          <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full" />
        </motion.button>
      )}
    </div>
  );
};
